import type { Honor, Rank, Suit } from '../game/types.js';
import { NUMERAL_FONT_PX, NUMERAL_FONT_STACK, TILE_INNER_PAD, WIND_LETTER_FONT_PX } from '../utils/constants.js';
import type { RenderPalette } from './palette.js';
import { getHonorSprite, getSuitSprite } from './tileSprites.js';

/**
 * Tile face bounds in the *local* (post-rotation) coordinate frame the caller
 * has already translated/rotated into, plus the scale factor derived from the
 * tile height. Every glyph metric multiplies by `scale` so a rack tile and an
 * edge-on opponent tile share one drawing routine.
 */
export interface TileBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly scale: number;
}

/** Suit tile: the face-art sprite (see `tileSprites.ts`) + a red corner
 *  numeral overlay — a legibility aid for a learner that authentic tiles
 *  don't carry, so it's drawn last, on top of the art. */
export function drawSuitContent(
  ctx: CanvasRenderingContext2D,
  b: TileBounds,
  suit: Suit,
  rank: Rank,
  palette: RenderPalette,
): void {
  const sprite = getSuitSprite(suit, rank);
  if (sprite) drawSprite(ctx, b, sprite);
  drawCornerNumeral(ctx, b, rank, palette);
}

/** Corner letter shown above the four wind tiles only — dragons show none. */
const WIND_LETTER: Partial<Record<Honor, string>> = {
  east: 'E',
  south: 'S',
  west: 'W',
  north: 'N',
};

/** Honor tile: the face-art sprite, plus a small red corner letter for winds
 *  (dragons carry no letter — same convention as a real set). */
export function drawHonorGlyph(
  ctx: CanvasRenderingContext2D,
  b: TileBounds,
  honor: Honor,
  palette: RenderPalette,
): void {
  const sprite = getHonorSprite(honor);
  if (sprite) drawSprite(ctx, b, sprite);

  const letter = WIND_LETTER[honor];
  if (letter !== undefined) {
    ctx.font = `700 ${WIND_LETTER_FONT_PX * b.scale}px ${NUMERAL_FONT_STACK}`;
    ctx.fillStyle = palette.cornerNumeralColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const pad = TILE_INNER_PAD * b.scale;
    ctx.fillText(letter, b.x + pad, b.y + pad);
  }
}

function drawCornerNumeral(
  ctx: CanvasRenderingContext2D,
  b: TileBounds,
  rank: Rank,
  palette: RenderPalette,
): void {
  ctx.font = `700 ${NUMERAL_FONT_PX * b.scale}px ${NUMERAL_FONT_STACK}`;
  ctx.fillStyle = palette.cornerNumeralColor;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  const pad = TILE_INNER_PAD * b.scale;
  ctx.fillText(String(rank), b.x + pad, b.y + pad);
}

/** Draw `img` contain-fit and centred within the tile face, inset by the same
 *  padding the corner numeral respects. */
function drawSprite(ctx: CanvasRenderingContext2D, b: TileBounds, img: HTMLImageElement): void {
  const pad = TILE_INNER_PAD * b.scale;
  const boxW = b.width - pad * 2;
  const boxH = b.height - pad * 2;
  const fit = Math.min(boxW / img.naturalWidth, boxH / img.naturalHeight);
  const w = img.naturalWidth * fit;
  const h = img.naturalHeight * fit;
  ctx.drawImage(img, b.x + (b.width - w) / 2, b.y + (b.height - h) / 2, w, h);
}
